/**
 * AmbientAutopilotPill — Project Harmony
 *
 * Autopilot is no longer a destination. It becomes ambient.
 * Every operational page carries this compact pill that shows:
 *   Marketing Department · 🟢 Active · Trust: Executive
 * Clicking it opens a drawer with:
 *   Trust Levels · Pause · Resume · Emergency Stop
 *
 * The drawer's Trust Level controls only appear once Autopilot has
 * been enabled at least once (Amendment 11 · Progressive Disclosure).
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertOctagon, ChevronDown, Pause, Play, Sparkles } from "lucide-react";

import {
  emergencyStopAutopilot,
  enableAutopilot,
  getAutopilotDashboard,
  pauseAutopilot,
  resumeAutopilot,
  setTrustLevel,
  type AutopilotDashboard,
  type TrustLevel,
} from "@/features/autopilot/api";
import { Button } from "@/shared/ui/button";
import { useWorkspaceMilestones } from "@/entities/workspace/milestones";
import { useCurrentWorkspace } from "@/entities/workspace";

const TRUST_LEVELS: { key: TrustLevel; label: string; tagline: string }[] = [
  { key: "advisor",   label: "Advisor",   tagline: "You approve everything." },
  { key: "assistant", label: "Assistant", tagline: "You approve publishing." },
  { key: "operator",  label: "Operator",  tagline: "You approve premium costs." },
  { key: "executive", label: "Executive", tagline: "You receive briefs." },
];

export function AmbientAutopilotPill(): JSX.Element | null {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const workspace = useCurrentWorkspace();
  const milestones = useWorkspaceMilestones(workspace?.id ?? null);

  const q = useQuery({
    queryKey: ["autopilot-dashboard"],
    queryFn: getAutopilotDashboard,
    refetchInterval: 30_000,
    retry: false,
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ["autopilot-dashboard"] });
  const enable = useMutation({ mutationFn: () => enableAutopilot(), onSuccess: refresh });
  const pause = useMutation({ mutationFn: () => pauseAutopilot(), onSuccess: refresh });
  const resume = useMutation({ mutationFn: () => resumeAutopilot(), onSuccess: refresh });
  const stop = useMutation({ mutationFn: () => emergencyStopAutopilot(), onSuccess: refresh });
  const trust = useMutation({
    mutationFn: (level: TrustLevel) => setTrustLevel(level),
    onSuccess: refresh,
  });

  // Patch 36.0.1 · mobile drawer polish. During the initial load the
  // pill renders a lightweight skeleton instead of returning null so
  // the Marketing Department section in the mobile drawer never blanks.
  if (q.isLoading || !q.data) {
    return (
      <div
        data-testid="ambient-autopilot-pill-skeleton"
        aria-busy="true"
        aria-label="Loading Marketing Department status"
        className="inline-flex items-center gap-2 rounded-full border border-[color:var(--md-line)] bg-[var(--md-ink-1)]/70 px-3 py-1.5 text-xs text-[var(--md-fog)]"
      >
        <span aria-hidden className="inline-block h-2 w-2 animate-pulse rounded-full bg-[color:var(--md-line)]" />
        <span>Marketing Dept</span>
        <span aria-hidden className="mx-1 h-3 w-px bg-[color:var(--md-line)]" />
        <span className="inline-block h-2 w-10 animate-pulse rounded bg-[color:var(--md-line)]" />
      </div>
    );
  }
  const d = q.data as AutopilotDashboard;

  const stateLabel =
    d.state === "enabled" ? "Active" :
    d.state === "paused" ? "Paused" : "Stopped";

  return (
    <div className="relative max-w-full">
      <button
        type="button"
        data-testid="ambient-autopilot-pill"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex max-w-full flex-wrap items-center justify-start gap-x-2 gap-y-0.5 rounded-2xl border border-[color:var(--md-line)] bg-[var(--md-ink-1)]/70 px-3 py-1.5 text-xs font-medium text-[var(--md-ivory-0)] transition hover:border-[var(--md-amber)] hover:bg-[var(--md-ink-1)]"
      >
        <span aria-hidden>{d.health_indicator}</span>
        <span>Marketing Dept ·</span>
        <span>{stateLabel}</span>
        <span aria-hidden className="mx-1 h-3 w-px bg-[color:var(--md-line)]" />
        <span className="text-[var(--md-fog)]">Trust</span>
        <span data-testid="ambient-autopilot-trust" className="truncate max-w-[110px]">{d.trust_label}</span>
        <ChevronDown className="h-3 w-3 text-[var(--md-fog)]" aria-hidden />
      </button>

      {open ? (
        <>
          {/* Click-away veil */}
          <div
            className="fixed inset-0 z-30"
            onClick={() => setOpen(false)}
            aria-hidden
          />
          <div
            data-testid="ambient-autopilot-drawer"
            className="absolute right-0 top-full z-40 mt-2 w-80 rounded-lg border border-[color:var(--md-line)] bg-[var(--md-ink-1)] p-4 shadow-2xl"
          >
            <div className="mb-3 flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest text-[var(--md-fog)]">
                  Marketing Department
                </div>
                <div className="text-sm font-semibold text-[var(--md-ivory-0)]">
                  {d.health_label} · {d.trust_label}
                </div>
              </div>
              <Sparkles className="h-4 w-4 text-[var(--md-amber)]" aria-hidden />
            </div>

            {milestones.hasAutopilotEnabled ? (
              <div className="space-y-1" data-testid="ambient-trust-options">
                <div className="mb-1 text-[10px] uppercase tracking-widest text-[var(--md-fog)]">
                  Trust Level
                </div>
                {TRUST_LEVELS.map((lvl) => {
                  const active = d.trust_level === lvl.key;
                  return (
                    <button
                      key={lvl.key}
                      type="button"
                      data-testid={`ambient-trust-${lvl.key}`}
                      onClick={() => {
                        trust.mutate(lvl.key);
                        setOpen(false);
                      }}
                      disabled={active || trust.isPending}
                      className={`w-full rounded-md border px-2 py-2 text-left text-xs transition
                        ${active
                          ? "border-[var(--md-amber)] bg-[var(--md-amber)]/10 text-[var(--md-ivory-0)]"
                          : "border-[color:var(--md-line)] text-[var(--md-fog)] hover:border-[var(--md-amber)]/40 hover:text-[var(--md-ivory-0)]"}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{lvl.label}</span>
                        {active ? <span className="text-[10px] text-[var(--md-amber)]">Current</span> : null}
                      </div>
                      <div className="text-[10px] text-[var(--md-fog)]">{lvl.tagline}</div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-md border border-dashed border-[color:var(--md-line)] p-3 text-xs text-[var(--md-fog)]">
                Enable Autopilot to hire your Marketing Department.
              </div>
            )}

            <div className="mt-3 flex flex-wrap gap-2 border-t border-[color:var(--md-line)] pt-3">
              {d.state === "enabled" ? (
                <Button
                  variant="outline" size="sm"
                  data-testid="ambient-autopilot-pause"
                  onClick={() => { pause.mutate(); setOpen(false); }}
                >
                  <Pause className="h-3.5 w-3.5 mr-1" aria-hidden /> Pause
                </Button>
              ) : (
                <Button
                  variant="outline" size="sm"
                  data-testid="ambient-autopilot-resume"
                  onClick={() => {
                    if (d.state === "stopped") enable.mutate();
                    else resume.mutate();
                    setOpen(false);
                  }}
                >
                  <Play className="h-3.5 w-3.5 mr-1" aria-hidden /> Resume
                </Button>
              )}
              <Button
                variant="ghost" size="sm"
                data-testid="ambient-autopilot-emergency"
                className="text-rose-500 hover:text-rose-600"
                onClick={() => { stop.mutate(); setOpen(false); }}
              >
                <AlertOctagon className="h-3.5 w-3.5 mr-1" aria-hidden /> Stop
              </Button>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
